<script lang="ts">
    import { onMount } from "svelte"
    import { kirbyFetch } from "../../api/kirby/api"
    import MainLayout from "../pagebuilder/MainLayout.svelte"
    import { getPageSelect } from "../../api/kirby/queries"
    import MiniLoader from "../widgets/MiniLoader.svelte"
    import NotFound from "./NotFound.svelte"
    import Index from "../SEO/Index.svelte"
    import { flattenBlocksForMainLayout } from "./helper"

    type HeroSlideLike = {
        title?: string
        subtitle?: string
        description?: string
        page_reference?: unknown[]
        background_image?: unknown[]
        background_video?: unknown[]
        secondary_links?: unknown[]
    }

    function slideHasContent(slide: HeroSlideLike | undefined) {
        if (!slide) return false

        const title = typeof slide.title === "string" ? slide.title.trim() : ""
        const subtitle = typeof slide.subtitle === "string" ? slide.subtitle.trim() : ""
        const description = typeof slide.description === "string" ? slide.description.trim() : ""
        const pageReference = Array.isArray(slide.page_reference) ? slide.page_reference.length : 0
        const backgroundImage = Array.isArray(slide.background_image) ? slide.background_image.length : 0
        const backgroundVideo = Array.isArray(slide.background_video) ? slide.background_video.length : 0
        const secondaryLinks = Array.isArray(slide.secondary_links) ? slide.secondary_links.length : 0

        return !!(
            title ||
            subtitle ||
            description ||
            pageReference ||
            backgroundImage ||
            backgroundVideo ||
            secondaryLinks
        )
    }

    function sanitizeHeroSlider(layout: Layout | undefined) {
        const heroBlock = layout?.columns?.[0]?.blocks?.[0] as { content?: { slides?: HeroSlideLike[] } } | undefined
        const slides = heroBlock?.content?.slides
        if (!layout || !Array.isArray(slides)) return layout

        const filteredSlides = slides.filter((slide) => slideHasContent(slide))
        if (filteredSlides.length === slides.length) return layout

        return {
            ...layout,
            columns: layout.columns.map((column, columnIndex) => {
                if (columnIndex !== 0) return column

                return {
                    ...column,
                    blocks: column.blocks.map((block, blockIndex) => {
                        if (blockIndex !== 0) return block

                        return {
                            ...block,
                            content: {
                                ...block.content,
                                slides: filteredSlides,
                            },
                        } as typeof block
                    }),
                }
            }),
        } as Layout
    }

    let page: KirbyPage = $state()
    let load = $state(true)
    let mainLayoutRows: Layout[] = $state([])
    let heroSliderBlock: Layout | undefined = $state()

    async function fetchArticles() {
        page = await kirbyFetch<KirbyPage>({
            query: "site.children.findBy('intendedTemplate','home')",
            sort: "date desc",
            select: getPageSelect(),
        })

        if (!page?.main_layout) {
            load = false
            heroSliderBlock = undefined
            mainLayoutRows = []
            return
        }

        const heroSliderBlockIdx = page.main_layout.findIndex((el) => el.columns[0].blocks[0].type === "heroSlider")
        load = false
        if (heroSliderBlockIdx === -1) {
            heroSliderBlock = undefined
            mainLayoutRows = [...(page.main_layout || [])]
            return
        }
        heroSliderBlock = sanitizeHeroSlider(page.main_layout[heroSliderBlockIdx])
        mainLayoutRows = page.main_layout.filter((_, idx) => idx !== heroSliderBlockIdx)
    }

    if (typeof window === "undefined") {
        void fetchArticles()
    } else {
        onMount(() => {
            void fetchArticles()
        })
    }
</script>

{#if load}
    <MiniLoader />
{:else if page}
    <Index title={page.meta_title || page.title} metaDescription={page.meta_description} noIndex={page.meta_noindex} />
    {#if heroSliderBlock}
        <MainLayout block={heroSliderBlock.columns[0].blocks[0]} />
    {/if}
    <main class="page">
        {#each flattenBlocksForMainLayout(mainLayoutRows) as block}
            <MainLayout {block} />
        {/each}
    </main>
{:else}
    <NotFound />
{/if}

<style lang="less">
</style>
